/* Load the next page when a sentinel element scrolls into view.
 *
 * IntersectionObserver rather than a scroll listener: the browser computes
 * visibility off the main thread, so this costs nothing per frame, whereas a
 * scroll handler measuring offsets runs on every pixel of movement.
 *
 * rootMargin starts the fetch 400px BEFORE the sentinel is visible, so the next
 * page is usually already there by the time someone reaches the bottom.
 */
import { useEffect, useRef } from 'react'

export function useInfiniteScroll(
  onLoadMore: () => void,
  { enabled = true, rootMargin = '400px' } = {},
) {
  const sentinel = useRef<HTMLDivElement>(null)
  // Held in a ref so changing the callback does not tear down and rebuild the
  // observer on every render.
  const handler = useRef(onLoadMore)
  handler.current = onLoadMore

  useEffect(() => {
    const el = sentinel.current
    if (!el || !enabled) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) handler.current()
      },
      { rootMargin },
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [enabled, rootMargin])

  return sentinel
}
